/*    
<summary>
  This functional component "AddCategoryManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../../../contexts/store-provider";
import { IAddCategoryManagement } from "../../../../models/forms/IAddCategory";
import { formatMessage } from "../../../../translations/format-message";
import AddCategoryManagementForm from "../forms/add-edit-category-form";

interface IProps {
  id: number;
  editMode: boolean;
  distinction: number;
  businessType: number;
  selectedDetails: any;
  selectedPath: string;
  orderLength: number;
  modalClosed: () => void;
}

const AddCategoryManagement = (props: IProps) => {
  const { categoryManagementStore } = useStore();
  const {
    GetCategoryManagementByIdService,
    resetAddUpdateCategoryManagementState,
    resetCategoryManagementDetail,
    categoryState,
    categoryDetail,
    addUpdateCategoryManagementState,
    AddCategoryManagementService,
    UpdateCategoryManagementService,
    resetDeleteCategoryManagement,
    inProgress,
    deleteCategoryManagementState,
  } = categoryManagementStore;
  const initialValues: IAddCategoryManagement = categoryDetail;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    if (props.editMode == true) {
      GetCategoryManagementByIdService(props.id);
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCategoryManagementState.success) {
      if (props.editMode == false)
        toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateCategoryManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "staffDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (categoryState.error) {
      toast.error(formatMessage(categoryState.error));
    }
  }, [categoryState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCategoryManagementState.error) {
      toast.error(formatMessage(addUpdateCategoryManagementState.error));
      resetAddUpdateCategoryManagementState();
    }
  }, [addUpdateCategoryManagementState.error]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    if (props.editMode == false) AddCategoryManagementService(values);
    else UpdateCategoryManagementService(values);
  };

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetCategoryManagementDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddCategoryManagementForm
        initialValues={initialValues}
        id={props.id}
        editMode={props.editMode}
        businessType={props.businessType}
        distinction={props.distinction}
        selectedPath={props.selectedPath}
        selectedDetails={props.selectedDetails}
        orderLength={props.orderLength}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        isLoading={addUpdateCategoryManagementState.inProgress || inProgress}
      />
    </React.Fragment>
  );
};

export default observer(AddCategoryManagement);
